import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '../../lib/utils';
import { 
    MapPin, 
    Briefcase, 
    Users, 
    Menu, 
    X, 
    LayoutDashboard,
    LogOut,
    Settings,
    Shield,
    CreditCard,
    BookOpen,
    FileCheck,
    GraduationCap
} from 'lucide-react';

export const Navbar = () => {
    const { user, isAuthenticated, isAdmin, isSuperAdmin, login, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { href: '/agencies', label: 'Find Recruiters', icon: MapPin },
        { href: '/multi-apply', label: 'Multi-Apply', icon: Briefcase },
        { href: '/jobs', label: 'Agency Jobs', icon: Briefcase },
        { href: '/cv-review', label: 'CV Review', icon: FileCheck },
        { href: '/practical-courses', label: 'Practical Courses', icon: GraduationCap },
        { href: '/blog', label: 'Blog', icon: BookOpen },
        { href: '/consultation', label: 'Consultation', icon: Users },
        { href: '/pricing', label: 'Pricing', icon: CreditCard },
    ];

    const isActive = (path) => location.pathname === path;

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link 
                        to="/" 
                        className="flex items-center gap-2 group"
                        data-testid="navbar-logo"
                    >
                        <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-heading font-bold text-xl text-neutral-900 group-hover:text-primary-600 transition-colors">
                            RecruitNearMe
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                    isActive(link.href)
                                        ? 'bg-primary-100 text-primary-700'
                                        : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Auth Section */}
                    <div className="hidden md:flex items-center gap-3">
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2 h-10"
                                        data-testid="user-menu-trigger"
                                    >
                                        <Avatar className="h-8 w-8">
                                            <AvatarImage src={user?.picture} alt={user?.name} />
                                            <AvatarFallback className="bg-primary-100 text-primary-700">
                                                {getInitials(user?.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-3 py-2">
                                        <p className="text-sm font-medium">{user?.name}</p>
                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full capitalize">
                                            {user?.subscription_tier}
                                        </span>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                                        <LayoutDashboard className="w-4 h-4 mr-2" />
                                        Dashboard
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                                        <Settings className="w-4 h-4 mr-2" />
                                        Settings
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                                                <Shield className="w-4 h-4 mr-2" />
                                                Admin Panel
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    {isSuperAdmin && (
                                        <DropdownMenuItem onClick={() => navigate('/super-admin')} data-testid="menu-super-admin">
                                            <Shield className="w-4 h-4 mr-2" />
                                            Super Admin
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Button 
                                    variant="ghost" 
                                    onClick={() => navigate('/login')}
                                    data-testid="login-btn"
                                >
                                    Sign In
                                </Button>
                                <Button 
                                    onClick={() => navigate('/login')}
                                    className="bg-primary-600 hover:bg-primary-700 btn-glow"
                                    data-testid="get-started-btn"
                                >
                                    Get Started
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2 rounded-lg hover:bg-neutral-100"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-btn"
                    >
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden border-t border-neutral-200 bg-white">
                    <div className="px-4 py-3 space-y-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                                    isActive(link.href)
                                        ? 'bg-primary-100 text-primary-700'
                                        : 'text-neutral-600 hover:bg-neutral-100'
                                }`}
                            >
                                <link.icon className="w-5 h-5" />
                                {link.label}
                            </Link>
                        ))}
                        <div className="pt-3 border-t border-neutral-200">
                            {isAuthenticated ? (
                                <>
                                    <Link
                                        to="/dashboard"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                                    >
                                        <LayoutDashboard className="w-5 h-5" />
                                        Dashboard
                                    </Link>
                                    {/* Admin Dashboard Link */}
                                    {(isAdmin || isSuperAdmin) && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-primary-600 hover:bg-primary-50"
                                        >
                                            <Shield className="w-5 h-5" />
                                            Admin Dashboard
                                        </Link>
                                    )}
                                    {/* Super Admin Dashboard Link */}
                                    {isSuperAdmin && (
                                        <Link
                                            to="/super-admin"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-amber-600 hover:bg-amber-50"
                                        >
                                            <Settings className="w-5 h-5" />
                                            Super Admin
                                        </Link>
                                    )}
                                    <button
                                        onClick={() => {
                                            handleLogout();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                                    >
                                        <LogOut className="w-5 h-5" />
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <div className="space-y-2">
                                    <Link 
                                        to="/login"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="block"
                                    >
                                        <Button variant="outline" className="w-full">
                                            Sign In
                                        </Button>
                                    </Link>
                                    <Link 
                                        to="/login"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="block"
                                    >
                                        <Button className="w-full bg-primary-600 hover:bg-primary-700">
                                            Sign Up
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};
